// ---- تاتا وصال ----
// Her model came rigged exactly as Jiddo's is — the same Hips, Spine02, neck,
// LeftToeBase — so everything his file learned about driving a humanoid works
// on her unchanged: the walk timed by the ground so her feet never skate, the
// hips recentred so a crossfade is not a slide.
//
// What she did NOT come with is an idle. Jiddo's loader refuses a model without
// one ("nothing to stand in; the card stays"), and hers ships Walking, Running
// and a single-frame baselayer. Held, that baselayer is a clean neutral stand —
// feet together, arms down — better than a paused walk frame, which leaves one
// foot forever forward. So it is her idle, with a slow breath written over the
// top so she is not a statue while she waits. Her Running clip is loaded and
// never asked for: the orbit keeps her within a few strides of the table.
//
// She makes the food. A finished word is her cue: she stops, turns to where it
// is going, says what she has made (MAKE, data.ts), and it leaves her hands.
import * as THREE from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {asset} from '../../core/assets';
import {clipMs,has,pickClip,sayPhrase} from '../../core/audio';
import {elder,elderBody,inBounds} from '../../core/field';
import {Animator,readHumanoid,stand} from '../../core/humanoid';
import {view} from '../../core/scene';
import {clamp,rnd,turnToward,wrap} from '../../core/util';
import {pack} from './pack';
import {FLIGHT,HOLD} from './dishes';
import {waiting} from './field';
import {SOFRA,edge,legClears,ring} from './set';

const WALK='Walking', IDLE='Armature|clip0|baselayer';
let anim:Animator|null=null, walkStride=2.15;
const TSPEED=1.0;                                // units a second
export const tataDebug={get clip(){return anim?.name??''},get model(){return anim?.model??null},get state(){return state}};

export function loadTata(){
  const file=pack().CAST.tata;
  new GLTFLoader().loadAsync(asset(file)).then(g=>{
    // 2.25 rather than Jiddo's 2.4: they read as a couple rather than as twins.
    const m=g.scene, rig=readHumanoid(m,2.25,g.animations,file); if(!rig)return;
    stand(m); anim=new Animator(m,g.animations,IDLE);
    walkStride=anim.stride(WALK,rig.toes,walkStride);
  }).catch(err=>console.warn(file+' did not load',err));
}
// ---- her brain: pause | walk | make ----
// She orbits her table rather than roaming the whole field: the food comes out
// of her hands and goes onto that table, so a grandmother who has wandered to
// the far trees when the word lands is a grandmother throwing from off screen.
// And she keeps moving — a stroll, a look at the table, another stroll — rather
// than standing at the rim between rounds: a grandmother parked at the edge of
// the table is a wall the boy has to get round to reach his food, and for a
// while she was exactly where he got stuck. Standing still, she faces it.
type State='pause'|'walk'|'make';
let state:State='pause', next=1800, tx=0, tz=0, walkT=0, phase=0;
let head=0, rot=0, makeUntil=-1e9, sayUntil=-1e9;
// She orbits the table rather than roaming: outside its edge, because that is
// where the food goes and she is not standing over it, and never far from it,
// because the camera is on her and the table has to stay in the picture. Both
// are how far off the table's EDGE (edge, set.ts): the inner one is her capsule
// (0.42) clear of the wood and a little more, and the ring is wide enough that
// a leg of it is a walk and not a shuffle.
const ORBIT_IN=0.55, ORBIT_OUT=1.55;
const PAUSE_MIN=400, PAUSE_MAX=1100;             // between strolls: a look at the table, not a wait
const LEG_MIN=0.9;                               // a stroll is at least this far, or it is a shuffle on the spot
// Somewhere else on the ring — and not just anywhere on it. Two things pick
// the spot, and both are about the picture rather than about her.
//
// She goes on the FAR side of the table from the camera, so that facing the
// table is facing roughly out of the screen: a grandmother seen over her own
// sofra rather than from behind.
//
// And she goes to one SIDE of it rather than dead behind it, because dead
// behind it is the middle of the frame, and the middle of the frame at head
// height is where the middle letter of an odd-length word hangs. On a phone
// held sideways that chip is a 86px white disc and her head was under it for
// the whole of every three-letter word. Off to one side she is at three
// quarters to the camera, which is a better angle on a face anyway.
// And she stays on whichever side she is already on. A spot is not a path:
// drawing the side afresh sent her from one side to the other, and the walk
// between them is a straight line through the middle — over the camera's centre
// line (so back under that chip, now while moving) and, worse, straight across
// the table, because two points on her ring either side of the camera's line
// have a chord that runs over the top. She would walk through the furniture. Held
// to one side, her legs are a 40° arc — and each one is checked for how near
// the table it PASSES, not just where it ends, so her hip never clips the top
// on the way.
const SIDE_MIN=0.75, SIDE_MAX=1.45;              // radians off the away-from-camera line: 43° to 83°
const CLEAR=0.42;                                // how near the table's edge a leg of hers may pass: her capsule off the wood
// On the grass, a walk away from where she stands, and not across the table:
// every spot she is ever sent to passes this, the go-round fallback included.
// The bounds are the engine's own, a stride in (inBounds, field.ts) — the field
// clamps BODIES and nobody clamps her, so a spot outside it is a spot she
// stays at.
function legOK(px:number,pz:number,x:number,z:number,min:number){
  if(!inBounds(x,z,0.6))return false;
  if(Math.hypot(x-px,z-pz)<min)return false;                                  // somewhere NEW, or it is not a walk
  return legClears(px,pz,x,z)>=CLEAR;                                         // or the leg would brush the table
}
// Somewhere to stroll, or null if there is nowhere at all — which is the
// caller's cue to stand for another beat rather than to "walk" on the spot.
function orbitSpot(){
  const rel=Math.atan2(elder.position.x-SOFRA.x,elder.position.z-SOFRA.z)-(view.yaw+Math.PI);
  const side=wrap(rel)>=0?1:-1;
  const px=elder.position.x, pz=elder.position.z;
  for(let i=0;i<14;i++){
    const {x,z}=ring(view.yaw+Math.PI+side*rnd(SIDE_MIN,SIDE_MAX),rnd(ORBIT_IN,ORBIT_OUT));
    if(legOK(px,pz,x,z,LEG_MIN))return {x,z};
  }
  // Nothing on her side. Either every leg to it runs across the top, or — with
  // the camera dragged a half turn — her side of the ring is off the near edge
  // of the field altogether. So she goes ROUND the table instead, a step at a
  // time, taking the first spot either way round that is on the grass and
  // clears the wood. Walking straight would take her through the furniture,
  // and the first draft of this fallback took the step whether or not it was
  // in the field: past the near bound, nothing walks her back, and every later
  // draw failed from there. She parked, which is the one thing this is for.
  const ae=Math.atan2(px-SOFRA.x,pz-SOFRA.z), re=edge(px,pz);
  const RS=[clamp(re,ORBIT_IN+0.1,ORBIT_OUT-0.1),ORBIT_IN+0.3,ORBIT_OUT-0.3];   // the radii as well: the way round may be a wider arc than the one she is on
  for(let k=1;k<=12;k++)for(const s of [side,-side])for(const r of RS){
    const {x,z}=ring(ae+s*k*0.26,r);
    if(legOK(px,pz,x,z,0.35))return {x,z};                                    // a step round is shorter than a stroll
  }
  // And a last resort: out to the middle of her ring, as straight away from
  // the table as the field allows. Nothing in the game puts her where this is
  // needed — it is for standing ON the table, where every leg starts too near
  // it for the clearance test to pass and she would stand there for good.
  for(let k=0;k<24;k++)for(const s of [1,-1]){
    const a=(Math.hypot(px-SOFRA.x,pz-SOFRA.z)>0.01?ae:0)+s*k*0.26, {x,z}=ring(a,(ORBIT_IN+ORBIT_OUT)/2);
    if(!inBounds(x,z,0.6))continue;
    if(Math.hypot(x-px,z-pz)<0.35)continue;
    return {x,z};
  }
  return null;
}
function faceTo(x:number,z:number){
  if(Math.hypot(x-elder.position.x,z-elder.position.z)<0.3)return;
  head=Math.atan2(x-elder.position.x,z-elder.position.z);
}
// Where the food leaves her: about chest height, a little in front of her.
export function hands(){
  const v=new THREE.Vector3(Math.sin(rot)*0.45,1.32,Math.cos(rot)*0.45);
  return v.add(elder.position);
}
// A word is finished, and this is the first thing that happens: she stops where
// she is and turns to face the table. The dish is already in her hands by now
// and rides them round, so the turn IS the wind-up — she holds it out over the
// sofra before she lets go of it, and she is still facing it as it lands. She
// is NOT still facing it when he arrives, and no longer tries to be: that is
// what parked her at the table for whole rounds. By then she is strolling her
// orbit, or paused looking at the spread.
export function aim(to:{x:number;z:number},t:number){
  state='make'; makeUntil=t+HOLD+FLIGHT+300; faceTo(to.x,to.z);   // she stands still until it has landed, answer() or no answer()
}
// Then she names what she has made. Returns how long she will be talking, so
// the round waits for her — and never less than the dish takes to land, so the
// next word is not dealt over the top of the throw.
export function make(to:{x:number;z:number},w:string,t:number){
  state='make'; makeUntil=t+1500; faceTo(to.x,to.z);
  const {MAKE,MAKE_ANY}=pack(), own=MAKE(w), line=has('R'+own)?own:pickClip(MAKE_ANY), at=260;
  sayPhrase(line,at);
  sayUntil=t+at+clipMs('R'+line);                // HER voice: what holds her still below
  return Math.max(at+clipMs('R'+line),HOLD+FLIGHT+400);
}
function brain(t:number,dt:number){
  // Bumped by a plate: she stops and thinks again — unless she is in the
  // middle of a throw, which nothing should be able to cancel. The dish rides
  // her hands, so walking off mid-hold would fling it from somewhere else.
  if(elderBody.bumped){elderBody.bumped=false;if(state!=='make'){state='pause';next=t+900;}}
  if(!anim)return false;
  // makeUntil covers the hold and the flight; sayUntil below is what keeps her
  // still while HER OWN mouth is moving. Waiting on speaking() as well held her
  // in 'make' through the whole hush — the gap between rounds and the next
  // word's name — which is most of a round.
  if(state==='make'){ if(t>makeUntil){state='pause';next=t+700;} return false; }
  // Her own line, and nothing else. This used to be the engine's talking(),
  // which is one flag moved by EVERY recorded line and every word said
  // (core/audio.ts) — including the boy's يمّ, whose takes run 3.6 to 5.5
  // seconds, and the round's own name, said twice. So she stood frozen through
  // the whole of his mouthful and most of the deal: the parked grandmother
  // this file set out to stop being, put there by a gate that reads as if it
  // were about her. It is about her now.
  if(t<sayUntil){ if(state==='walk'){state='pause';} next=Math.max(next,t+400); return false; }
  if(state==='pause'){
    // Standing still, she watches the table — which is where she has just put
    // the food and where he is coming to eat it. It costs a line and it is the
    // difference between a grandmother waiting for her grandson and a
    // grandmother staring off at a tree.
    // What she looks at is the newest dish nobody has eaten (waiting,
    // field.ts) — and the table if every one of them has been. With one dish
    // out, which is what a round leaves, that is the one he is coming for, so
    // she is already looking where he is going; with two, he takes whichever
    // is nearest HIM (nearest, boy.ts) and she may be watching the other.
    const out=waiting();
    if(out)faceTo(out.position.x,out.position.z); else faceTo(SOFRA.x,SOFRA.z);
    // She used to stand there for as long as food was out, so as to be still
    // facing it when he arrived. Facing it while he eats is not worth what it
    // cost: a grandmother parked at the rim for the whole round is a wall
    // between him and his food, and he got stuck against her. So she strolls
    // her orbit whatever is on the table, pausing only for a look at it —
    // and when he arrives she is wherever she is, which is what a grandmother
    // is when the grandson comes to the table.
    if(t<next)return false;
    // Nowhere to go — she is boxed in — so she stands another beat rather than
    // entering 'walk' for a spot she is already on. That one frame of 'walk'
    // was visible: play(WALK) pins the clip at time 0 and the crossfade back
    // to the idle starts from full weight, so she snapped into a mid-stride
    // pose and eased out of it, every pause.
    const s=orbitSpot();
    if(!s){next=t+600;return false;}
    tx=s.x; tz=s.z; state='walk'; walkT=t; phase=0; return false;
  }
  const dx=tx-elder.position.x, dz=tz-elder.position.z, d=Math.hypot(dx,dz);
  if(d<0.2||t-walkT>9000){state='pause';next=t+rnd(PAUSE_MIN,PAUSE_MAX);return false;}
  head=Math.atan2(dx,dz);
  const v=Math.min(d,TSPEED*dt);
  elder.position.x+=dx/d*v; elder.position.z+=dz/d*v;
  phase+=v/walkStride;
  return true;
}
export function updateTata(t:number,dt:number){
  elderBody.walking=brain(t,dt);
  if(anim){
    const w=state==='walk'?WALK:IDLE; if(w!==anim.name)anim.play(w,0.28);
    anim.update(dt,anim.name===WALK?phase:undefined);
    rot=turnToward(rot,head,4*dt); elder.rotation.y=rot;
    // The breath. Her idle is one held frame, so without this she is a statue:
    // a slow rise and fall of a couple of centimetres, and the faintest sway.
    if(anim.name===IDLE){elder.position.y=0.018*Math.sin(t*0.0021);elder.rotation.z=0.006*Math.sin(t*0.0017);}
    else {elder.position.y=0;elder.rotation.z=0;}
  }
}
